import React from 'react';
import { Bar, BarConfig } from '@ant-design/plots';
import {
  numberFormatter,
  numberShortFormatter,
} from '../utils/numberFormatter';
import { chartData } from '../pages/risk-hub/components/ExposuresChart';
import { useUserContext } from '../context/PermissionsContext';

interface ChartProps {
  data: chartData[];
}

function Chart({ data }: ChartProps) {
  const { canViewFinancials } = useUserContext();

  const width = window.innerWidth || 0;
  const [containerWidth, setContainerWidth] = React.useState(width);
  React.useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const config: BarConfig = {
    data,
    isGroup: true,
    xField: 'value',
    yField: 'label',
    isStack: true,
    scrollbar: {
      type: 'vertical',
    },
    seriesField: 'type',
    xAxis: false,
    yAxis: {
      label: {
        offset: 40,
        style: {
          fontWeight: 400,
          fontSize: 14,
          fontFamily: 'Montserrat, sans-serif',
          color: '#3D5A80',
          fill: '#3D5A80',
        },
      },
      line: {
        style: {
          stroke: '#979797',
        },
      },
      position: 'bottom',
    },
    legend: {
      position: 'top-right',
      reversed: true,
      itemName: {
        style: {
          fontWeight: 500,
          fontSize: 10,
          fontFamily: 'Montserrat, sans-serif',
          color: '#676767',
        },
      },
      marker: {
        symbol: 'circle',
        style: {
          r: 6,
        },
      },
    },
    barStyle: {
      radius: [20, 20, 0, 0],
    },
    color: ['#CDCDCD', '#437EF7'],
    tooltip: {
      domStyles: {
        'g2-tooltip': {
          backgroundColor: '#FFF',
          color: '#3D5A80',
          border: '1px solid #F1FAEE',
          fontFamily: 'Montserrat, sans-serif',
        },
      },
      formatter: (datum) => {
        return {
          name: datum.type,
          value: canViewFinancials ? numberFormatter(datum.value) : '',
        };
      },
    },

    label: {
      position: 'right',
      offsetX: -2,
      style: {
        fontFamily: 'Montserrat, sans-serif',
        backgroundColor: '#FFF',
        fill: '#000',
        stroke: '#000',
        fontSize: 12,
        textAlign: 'left',
      },
      autoRotate: true,
      formatter: (datum) => {
        return canViewFinancials ? numberShortFormatter(datum.value) : '';
      },
    },

    marginRatio: 0.3, // Increase space between grouped bars
    padding: [30, 100, 30, 300], // Adjust padding to give more space around the chart
    height: 350, // Increase chart height to allow more space for bars
    limitInPlot: true,
    // barWidthRatio: 0.7,
  };

  return <Bar key={containerWidth} {...config} />;
}

export default Chart;
